// ============================================================================
//  invoices.api.ts — Couche API Facturation
//  Conformité CGI SN : numérotation, NINEA, avoir, FEC
// ============================================================================

import api from '@/lib/axios'

// ─── Types ───────────────────────────────────────────────────────────────────

export type InvoiceType   = 'INVOICE' | 'CREDIT_NOTE' | 'DEBIT_NOTE' | 'PROFORMA' | 'ADVANCE'
export type InvoiceStatus = 'DRAFT' | 'ISSUED' | 'SENT' | 'PARTIAL_PAID' | 'PAID' | 'CANCELLED' | 'DISPUTED'
export type PaymentMethod = 'BANK_TRANSFER' | 'CHECK' | 'CASH' | 'MOBILE_MONEY'

export interface InvoiceLine {
  id:          string
  lineNumber:  number
  description: string
  quantity:    number
  unitPrice:   number
  discount:    number
  amountHt:    number
  taxRate:     number
  taxAmount:   number
  amountTtc:   number
  accountCode?: string
  taxRule?:    { id: string; code: string; name: string; rate: number }
}

export interface InvoicePayment {
  id:        string
  amount:    number
  currency:  string
  method:    PaymentMethod
  reference?: string
  paidAt:    string
  notes?:    string
}

export interface Invoice {
  id:               string
  number:           string
  sequence:         number
  fiscalYearSeq:    number
  type:             InvoiceType
  status:           InvoiceStatus
  issueDate?:       string
  dueDate?:         string
  issuedAt?:        string
  isNumberLocked:   boolean
  clientName:       string
  clientNinea?:     string
  clientRccm?:      string
  clientAddress?:   string
  clientEmail?:     string
  subtotalHt:       number
  totalTax:         number
  totalTtc:         number
  currency:         string
  notes?:           string
  createdAt:        string
  lines:            InvoiceLine[]
  payments:         InvoicePayment[]
  originalInvoice?: { number: string; status: InvoiceStatus }
  crmCompany?:      { name: string }
  crmContact?:      { firstName: string; lastName: string }
}

export interface InvoiceStats {
  draft:        number
  issued:       number
  paid:         number
  overdue:      number
  totalRevenue: number
}

// Règle de TVA de vente exposée par GET /invoices/tax-rules (rate en %, ex. 18)
export interface SalesTaxRule {
  id:               string
  code:             string
  name:             string
  type:             string
  rate:             number
  isExempt:         boolean
  exemptionReason?: string
  territoriality?:  string
  legalReference?:  string
}

export interface CreateInvoiceLineInput {
  description:  string
  quantity:     number
  unitPrice:    number
  discount?:    number
  taxRuleCode?: string
  accountCode?: string
}

export interface CreateInvoiceInput {
  type?:         InvoiceType
  clientName:    string
  clientNinea?:  string
  clientRccm?:   string
  clientAddress?: string
  clientEmail?:  string
  issueDate?:    string
  dueDate?:      string
  crmCompanyId?: string
  crmContactId?: string
  opportunityId?: string
  fiscalYearId?: string
  lines:         CreateInvoiceLineInput[]
  notes?:        string
}

export interface CreateCreditNoteInput {
  originalInvoiceId: string
  reason:            string
  lines:             CreateInvoiceLineInput[]
  notes?:            string
}

export interface RecordPaymentInput {
  amount:     number
  method:     PaymentMethod
  reference?: string
  paidAt:     string
  notes?:     string
}

export interface ListInvoicesParams {
  status?:       InvoiceStatus
  type?:         InvoiceType
  search?:       string
  fiscalYearId?: string
  crmCompanyId?: string
  dateFrom?:     string
  dateTo?:       string
  page?:         number
  limit?:        number
}

export interface PaginatedInvoices {
  data: Invoice[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

export interface FecLine {
  journalCode:    string
  journalLabel:   string
  entryNumber:    string
  entryDate:      string
  accountCode:    string
  accountLabel:   string
  debit:          number
  credit:         number
  description:    string
  reference?:     string
}

export interface FecExport {
  lines:       FecLine[]
  totalLines:  number
  fiscalYear:  { year: number; label: string }
  generatedAt: string
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const invoicesApi = {
  // Dashboard
  getStats: (): Promise<InvoiceStats> =>
    api.get('/invoices/stats').then(r => r.data.data ?? r.data),

  // Liste
  list: (params?: ListInvoicesParams): Promise<PaginatedInvoices> =>
    api.get('/invoices', { params }).then(r => r.data.data ?? r.data),

  // Détail
  getOne: (id: string): Promise<Invoice> =>
    api.get(`/invoices/${id}`).then(r => r.data.data ?? r.data),

  // Création
  create: (data: CreateInvoiceInput): Promise<Invoice> =>
    api.post('/invoices', data).then(r => r.data.data ?? r.data),

  // Modification (DRAFT uniquement)
  update: (id: string, data: Partial<CreateInvoiceInput>): Promise<Invoice> =>
    api.patch(`/invoices/${id}`, data).then(r => r.data.data ?? r.data),

  // Émission
  issue: (id: string): Promise<Invoice> =>
    api.post(`/invoices/${id}/issue`).then(r => r.data.data ?? r.data),

  // Envoi
  markAsSent: (id: string): Promise<Invoice> =>
    api.post(`/invoices/${id}/send`).then(r => r.data.data ?? r.data),

  // Avoir
  createCreditNote: (data: CreateCreditNoteInput): Promise<Invoice> =>
    api.post('/invoices/credit-note', data).then(r => r.data.data ?? r.data),

  // Paiement
  recordPayment: (invoiceId: string, data: RecordPaymentInput): Promise<InvoicePayment> =>
    api.post(`/invoices/${invoiceId}/payments`, data).then(r => r.data.data ?? r.data),

  // Jeton d'impression (5 min, usage unique) pour ouvrir le HTML de la facture
  // via `?pt=` — jamais l'access token dans l'URL (audit M1)
  getPrintToken: (id: string): Promise<{ token: string; expiresIn: number }> =>
    api.post(`/invoices/${id}/print-token`).then(r => r.data.data ?? r.data),

  // Règles de TVA de vente (lecture seule, permission invoices:view)
  // taxRate / rate exprimés en POURCENTAGE (18 = 18 %)
  getTaxRules: (): Promise<SalesTaxRule[]> =>
    api.get('/invoices/tax-rules').then(r => r.data.data ?? r.data),

  // Export FEC
  exportFec: (fiscalYearId: string): Promise<FecExport> =>
    api.get(`/invoices/export/fec/${fiscalYearId}`).then(r => r.data.data ?? r.data),
}
